import React from "react";
import { motion } from "framer-motion";
import {
  Container,
  Typography,
  Box,
  IconButton,
  Link,
  useTheme,
} from "@mui/material";
import { Facebook, Instagram, LinkedIn, Email } from "@mui/icons-material";

import logo from "../../assets/NC-Logo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const theme = useTheme();

  return (
    <footer
      style={{
        backgroundColor: theme.palette.primary.black,
        color: "white",
        width: "100%",
        marginTop: "auto",
        paddingTop: "10px",
        paddingBottom: "10px",
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 4, md: 0 },
          }}
        >
          {/* Лого в ляво */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: { xs: "center", md: "flex-start" },
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box
                sx={{
                  height: "50px",
                  width: "auto",
                  maxWidth: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  filter: "brightness(0) invert(1)",
                }}
              >
                <img
                  src={logo}
                  alt="Народни Читалища Лого"
                  style={{
                    height: "100%",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </motion.div>
          </Box>

          {/* Социални бутони в средата */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <IconButton
                  size="medium"
                  sx={{
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    padding: "8px",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      transform: "translateY(-2px)",
                    },
                  }}
                  href="https://www.facebook.com/narodnichitalishta/?locale=bg_BG"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook fontSize="medium" />
                </IconButton>

                <IconButton
                  size="medium"
                  sx={{
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    padding: "8px",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      transform: "translateY(-2px)",
                    },
                  }}
                  href="https://www.linkedin.com/company/narodni-chitalishta-foundation/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedIn fontSize="medium" />
                </IconButton>

                <IconButton
                  component="a"
                  href="mailto:info@narodnichitalishta.bg"
                  size="medium"
                  sx={{
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    transition: "all 0.3s",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      transform: "translateY(-2px)",
                    },
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                  }}
                >
                  <Email fontSize="medium" />
                </IconButton>
              </Box>
            </motion.div>
          </Box>

          {/* Copyright и разработка в дясно */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              justifyContent: { xs: "center", md: "flex-end" },
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "center", md: "flex-end" },
                  gap: 1.5, // Увеличих разстоянието между редовете
                  paddingTop: { md: 0.5 }, // Добавих отгоре за по-добро разпределение
                  paddingBottom: { md: 0.5 }, // Добавих отдолу
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#bbbbbb",
                    textAlign: { xs: "center", md: "right" },
                    fontWeight: 400,
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                  }}
                >
                  © {currentYear} Фондация "Народни Читалища".
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#bbbbbb",
                    textAlign: { xs: "center", md: "right" },
                    fontWeight: 400,
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                  }}
                >
                  Разработено от{" "}
                  <Link
                    href="https://ludogoriesoft.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#bbbbbb",
                      textDecoration: "underline",
                      "&:hover": {
                        color: "white",
                        // textDecoration: "underline",
                      },
                    }}
                  >
                    Ludogorie Soft
                  </Link>
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </Box>

        {/* Мобилна версия */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            mt: 4,
          }}
        >
          {/* Социални бутони за мобилни */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <IconButton
              size="medium"
              sx={{
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                padding: "10px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
              href="https://www.facebook.com/narodnichitalishta/?locale=bg_BG"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Facebook fontSize="medium" />
            </IconButton>

            <IconButton
              size="medium"
              sx={{
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                padding: "10px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
              href="https://www.linkedin.com/company/narodni-chitalishta-foundation/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedIn fontSize="medium" />
            </IconButton>

            <IconButton
              component="a"
              href="mailto:info@narodnichitalishta.bg"
              size="medium"
              sx={{
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "translateY(-2px)",
                },
                width: 40,
                height: 40,
              }}
            >
              <Email fontSize="medium" />
            </IconButton>
          </Box>

          {/* Текст за мобилни */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5, // Същото увеличение на разстоянието и за мобилна версия
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#bbbbbb",
                textAlign: "center",
                fontSize: "0.85rem",
                lineHeight: 1.5,
              }}
            >
              © {currentYear} Фондация "Народни Читалища".
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#bbbbbb",
                textAlign: "center",
                fontSize: "0.85rem",
                lineHeight: 1.5,
              }}
            >
              Разработено от{" "}
              <Link
                href="https://ludogoriesoft.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#bbbbbb",
                  textDecoration: "none",
                  "&:hover": {
                    color: "white",
                    textDecoration: "underline",
                  },
                }}
              >
                Ludogorie Soft
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </footer>
  );
};

export default Footer;
